import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { PawPrint, Heart, Menu, X, ArrowRight, MessageCircle } from "lucide-react";
import { useFavorites } from "@/lib/favorites";

const nav = [
  { to: "/quiz", label: "Take the quiz", desc: "Get your ranked breed matches" },
  { to: "/breeds", label: "Browse breeds", desc: "Filter by size, energy, budget & more" },
  { to: "/guides?category=training", label: "Dog training", desc: "Recall, reactivity, house training & more" },
  { to: "/guides", label: "Care guides", desc: "First-week basics & ownership help" },
  { to: "/adoption", label: "Adopt", desc: "Dogs waiting near you" },
  { to: "/breeders", label: "Breeders", desc: "Registered, ethical breeders" },
  { to: "/play", label: "Fun quizzes", desc: "10-second games, just for fun" },
  { to: "/upgrade", label: "Unlock full results — $2", desc: "See all your matches & compare" },
];

export default function Layout({ children }) {
  const { favorites } = useFavorites();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 rounded-full bg-primary text-primary-foreground grid place-items-center">
              <PawPrint size={18} strokeWidth={2.4} />
            </span>
            <span className="font-display text-xl font-bold text-accent tracking-tight">Fetchright</span>
          </Link>

          <div className="flex items-center gap-3">
            <Link
              to="/saved"
              data-testid="saved-link"
              className="pill-btn relative inline-flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-4 py-2 text-sm font-semibold hover:bg-muted"
              aria-label="Saved matches"
            >
              <Heart size={15} />
              <span className="hidden sm:inline">Saved</span>
              {favorites.length > 0 && (
                <span
                  data-testid="saved-count-badge"
                  className="ml-0.5 min-w-[20px] h-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold grid place-items-center"
                >
                  {favorites.length}
                </span>
              )}
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="menu-toggle"
              aria-expanded={menuOpen}
              aria-label="Menu"
              className="pill-btn inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold"
            >
              {menuOpen ? <X size={16} /> : <Menu size={16} />}
              <span className="hidden sm:inline">Menu</span>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div
            data-testid="menu-panel"
            className="absolute inset-x-0 top-16 border-b border-border/60 bg-background/95 backdrop-blur-xl shadow-lg animate-in fade-in slide-in-from-top-2 duration-200"
          >
            <nav className="max-w-7xl mx-auto px-6 lg:px-10 py-6 grid sm:grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="menu-ask-advisor"
                onClick={() => {
                  setMenuOpen(false);
                  window.dispatchEvent(new Event("open-advisor"));
                }}
                className="sm:col-span-2 group flex items-center justify-between rounded-2xl px-4 py-3 bg-primary text-primary-foreground text-left"
              >
                <span className="flex items-center gap-3">
                  <MessageCircle size={20} className="shrink-0" />
                  <span>
                    <span className="block font-display font-bold">Ask the advisor</span>
                    <span className="block text-xs text-primary-foreground/80">
                      Get answers to any dog question — free
                    </span>
                  </span>
                </span>
                <ArrowRight size={16} className="shrink-0 group-hover:translate-x-1 transition-transform" />
              </button>
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.to.slice(1)}`}
                  className={({ isActive }) =>
                    `group flex items-center justify-between rounded-2xl px-4 py-3 transition-colors ${
                      isActive ? "bg-secondary" : "hover:bg-secondary/60"
                    }`
                  }
                >
                  <span>
                    <span className="block font-display font-bold text-accent">{n.label}</span>
                    <span className="block text-xs text-muted-foreground">{n.desc}</span>
                  </span>
                  <ArrowRight
                    size={16}
                    className="text-primary shrink-0 group-hover:translate-x-1 transition-transform"
                  />
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-24 border-t border-border/60 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row gap-6 md:items-center justify-between text-sm text-muted-foreground">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-accent">
              <PawPrint size={16} strokeWidth={2.4} />
              <span className="font-display font-bold">Fetchright</span>
            </div>
            <p className="max-w-md text-xs leading-relaxed">
              Guidance, not veterinary advice. Always consult a licensed vet for medical concerns.
            </p>
          </div>
          <nav
            className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
            data-testid="footer-legal-nav"
          >
            <Link to="/play" className="hover:text-accent transition-colors" data-testid="footer-play">
              Fun quizzes
            </Link>
            <Link to="/terms" className="hover:text-accent transition-colors" data-testid="footer-terms">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-accent transition-colors" data-testid="footer-privacy">
              Privacy
            </Link>
            <Link to="/refunds" className="hover:text-accent transition-colors" data-testid="footer-refunds">
              Refunds
            </Link>
            <a
              href="mailto:support@fetchright.net"
              className="hover:text-accent transition-colors"
              data-testid="footer-contact"
            >
              Contact
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
